// Réapprovisionnement intelligent — alertes de stock basées sur les prévisions IA
import { useState, useMemo } from "react";
import { Package, ShoppingCart, AlertTriangle } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { KPICard } from "@/components/admin/shared/KPICard";
import { useReplenishmentAlerts } from "@/hooks/admin/useAI";
import type { ReplenishmentAlert, ReplenishmentUrgency } from "@/lib/admin-api/ai";
import React from "react";

// ---------------------------------------------------------------------------
// Urgency helpers
// ---------------------------------------------------------------------------

const URGENCY_BADGE: Record<ReplenishmentUrgency, string> = {
  critical: "bg-red-100 text-red-700",
  order_soon: "bg-orange-100 text-orange-700",
  watch: "bg-amber-100 text-amber-700",
  ok: "bg-green-100 text-green-700",
};

const URGENCY_LABELS: Record<ReplenishmentUrgency, string> = {
  critical: "Critique",
  order_soon: "Commander bientôt",
  watch: "Surveiller",
  ok: "OK",
};

const URGENCY_PULSE: Record<ReplenishmentUrgency, boolean> = {
  critical: true,
  order_soon: false,
  watch: false,
  ok: false,
};

const URGENCY_LEVELS: readonly { value: ReplenishmentUrgency; label: string }[] = [
  { value: "critical", label: "Critique" },
  { value: "order_soon", label: "Commander bientôt" },
  { value: "watch", label: "Surveiller" },
  { value: "ok", label: "OK" },
];

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function Replenishment() {
  const [urgencyFilter, setUrgencyFilter] = useState<ReplenishmentUrgency | "">("");

  const params = useMemo(
    () => ({
      urgency: (urgencyFilter || null) as ReplenishmentUrgency | null,
    }),
    [urgencyFilter],
  );

  const { data, isLoading, isError } = useReplenishmentAlerts(params);

  const alerts = data?.alerts ?? [];
  const criticalCount = data?.critical_count ?? 0;
  const orderSoonCount = alerts.filter((a) => a.urgency === "order_soon").length;
  const avgDaysStock =
    alerts.length > 0
      ? Math.round(
          alerts.reduce((sum, a) => sum + a.days_of_stock_remaining, 0) / alerts.length,
        )
      : 0;

  const columns: readonly ColumnDef<ReplenishmentAlert>[] = useMemo(
    () => [
      {
        id: "sku",
        header: "SKU",
        cell: (row) => <span className="font-mono text-xs text-stone-500">{row.sku}</span>,
      },
      {
        id: "name",
        header: "Produit",
        cell: (row) => (
          <div>
            <p className="text-sm font-medium text-stone-900">{row.name}</p>
            <p className="text-xs text-stone-400">{row.brand}</p>
          </div>
        ),
      },
      {
        id: "current_stock",
        header: "Stock actuel",
        cell: (row) => (
          <span
            className={`text-sm font-medium ${
              row.urgency === "critical"
                ? "text-red-600"
                : row.urgency === "order_soon"
                  ? "text-orange-600"
                  : "text-stone-900"
            }`}
          >
            {row.current_stock}
          </span>
        ),
      },
      {
        id: "avg_daily_sales",
        header: "Ventes/jour",
        cell: (row) => (
          <span className="text-sm text-stone-700">{row.avg_daily_sales.toFixed(1)}</span>
        ),
      },
      {
        id: "days_of_stock_remaining",
        header: "Jours restants",
        cell: (row) => (
          <span
            className={`text-sm font-medium ${
              row.days_of_stock_remaining <= 3
                ? "text-red-600"
                : row.days_of_stock_remaining <= 7
                  ? "text-orange-600"
                  : "text-stone-700"
            }`}
          >
            {row.days_of_stock_remaining}j
          </span>
        ),
      },
      {
        id: "urgency",
        header: "Urgence",
        cell: (row) => (
          <span
            className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${URGENCY_BADGE[row.urgency]} ${
              URGENCY_PULSE[row.urgency] ? "animate-pulse" : ""
            }`}
          >
            {URGENCY_LABELS[row.urgency]}
          </span>
        ),
      },
      {
        id: "suggested_qty",
        header: "Qté suggérée",
        cell: (row) => (
          <span className="text-sm font-semibold text-stone-900">{row.suggested_qty}</span>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: (row) =>
          row.urgency === "ok" ? null : (
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md bg-stone-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-stone-800"
            >
              <ShoppingCart className="h-3.5 w-3.5" />
              Commander
            </button>
          ),
      },
    ],
    [],
  );

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Réapprovisionnement</h1>
          <p className="mt-1 text-sm text-stone-500">
            Alertes de stock générées par l'IA à partir des prévisions de demande
          </p>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <KPICard
            label="Alertes critiques"
            value={isLoading ? "…" : String(criticalCount)}
            icon={<AlertTriangle className="h-4 w-4" />}
          />
          <KPICard
            label="À commander bientôt"
            value={isLoading ? "…" : String(orderSoonCount)}
            icon={<ShoppingCart className="h-4 w-4" />}
          />
          <KPICard
            label="Jours de stock moyens"
            value={isLoading ? "…" : `${avgDaysStock}j`}
            icon={<Package className="h-4 w-4" />}
          />
        </div>

        {/* Filter */}
        <div className="flex flex-wrap gap-3">
          <select
            value={urgencyFilter}
            onChange={(e) => setUrgencyFilter(e.target.value as ReplenishmentUrgency | "")}
            className="rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-stone-500 focus:outline-none"
          >
            <option value="">Toutes les urgences</option>
            {URGENCY_LEVELS.map((u) => (
              <option key={u.value} value={u.value}>
                {u.label}
              </option>
            ))}
          </select>
        </div>

        {/* Table */}
        {isError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Impossible de charger les alertes. Veuillez réessayer.
          </div>
        ) : (
          <AdminDataTable
            data={alerts}
            columns={columns}
            isLoading={isLoading}
            emptyMessage="Aucune alerte de réapprovisionnement."
            getRowId={(row) => row.product_id}
          />
        )}
      </div>
    </AdminLayout>
  );
}
